// frontend/src/marketing/data/solutions.js
//
// Content model for the public Solutions / "practical ideas" pages. Each entry
// drives one /solutions/:slug page via SolutionTemplate, and the cards on the
// /solutions index. Ideas are grounded in real, current QR use-cases.
import {
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  HomeModernIcon,
  TicketIcon,
  CubeIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";

export const SOLUTIONS = [
  {
    slug: "retail",
    name: "Retail",
    icon: ShoppingBagIcon,
    eyebrow: "QR codes for retail",
    headline: "Turn shelf space into a sales channel",
    subhead:
      "Bridge the gap between physical product and digital experience — and change where every code points without reprinting a single label.",
    problem: {
      title: "Shoppers decide at the shelf",
      body: "When a customer picks up a product they want sizing, reviews, ingredients or a deal — instantly. A static label can't deliver that, and a reprint costs weeks. Dynamic QR codes put the answer one scan away and let you update it on the fly.",
    },
    stat: { value: "~15%", label: "average sales lift reported with scan-to-pay" },
    ideas: [
      { title: "Product detail on demand", body: "Link packaging to specs, sizing guides, compatibility and how-to videos so shoppers self-serve at the shelf." },
      { title: "Scan-to-pay & checkout", body: "Send shoppers straight to a mobile checkout or payment page to cut queues and abandoned carts." },
      { title: "Reviews & social proof", body: "Point a code at verified reviews or UGC to build confidence at the moment of decision." },
      { title: "Loyalty sign-up", body: "Make joining the loyalty programme a single scan at the till or on the receipt." },
      { title: "Restock & reorder", body: "Let customers reorder a consumable in seconds — a code that always points at the current product page." },
    ],
    qrTypes: ["URL", "vCard", "Email"],
    features: [
      { title: "Dynamic redirects", body: "Swap a promo for evergreen content the day a sale ends — same printed code." },
      { title: "A/B testing", body: "Split traffic between two landing pages and keep the one that converts." },
      { title: "Location heatmaps", body: "See which stores and regions drive the most scans." },
    ],
  },
  {
    slug: "restaurants",
    name: "Restaurants",
    icon: BuildingStorefrontIcon,
    eyebrow: "QR codes for restaurants & hospitality",
    headline: "Menus you can update mid-service",
    subhead:
      "From table-side menus to feedback and WiFi, give guests a frictionless experience and change everything from your phone.",
    problem: {
      title: "Printed menus go stale instantly",
      body: "Prices change, dishes sell out, specials rotate daily. Reprinting menus is slow and wasteful. A dynamic QR menu lets the kitchen update the page weekly — or hourly — with no new print run.",
    },
    stat: { value: "0", label: "reprints needed to change a dynamic menu" },
    ideas: [
      { title: "Digital menus", body: "A code on each table opens an always-current menu — update dishes, prices and photos any time." },
      { title: "Table tents & specials", body: "Promote the daily special or a seasonal cocktail and redirect the same code when it changes." },
      { title: "Reservations & waitlist", body: "Let walk-ins join the waitlist or book a table by scanning at the door." },
      { title: "Feedback at the table", body: "Capture a quick review while the experience is fresh — route it to your preferred platform." },
      { title: "Guest WiFi", body: "A WiFi code connects guests instantly without typing a password." },
    ],
    qrTypes: ["URL", "WiFi", "vCard"],
    features: [
      { title: "Dynamic redirects", body: "Pull a sold-out dish or push a happy-hour page without touching the table tents." },
      { title: "Folders & tags", body: "Organise codes by venue, floor or campaign across multiple locations." },
      { title: "Real-time analytics", body: "See which tables and times drive the most menu scans." },
    ],
  },
  {
    slug: "real-estate",
    name: "Real estate",
    icon: HomeModernIcon,
    eyebrow: "QR codes for real estate",
    headline: "Every sign becomes an open house",
    subhead:
      "Let passers-by tour a property, grab the brochure and book a viewing — straight from a yard sign or flyer.",
    problem: {
      title: "Interest fades after the drive-by",
      body: "A buyer notices a “For Sale” sign but has no easy way to learn more in the moment. A QR code turns that curiosity into a virtual tour, a booked viewing, or a saved listing before they drive on.",
    },
    stat: { value: "24/7", label: "self-serve tours from a single sign" },
    ideas: [
      { title: "For-sale sign tours", body: "Scan the yard sign to watch a video walkthrough and see full listing details." },
      { title: "Open-house check-in", body: "Capture visitor contact details with a scan instead of a paper sheet." },
      { title: "Brochure & floor plans", body: "Link printed flyers to high-res photos, floor plans and the price history." },
      { title: "Book a viewing", body: "Send prospects straight to your scheduling page to lock in a slot." },
      { title: "Agent vCard", body: "One scan saves the agent's contact details to the buyer's phone." },
    ],
    qrTypes: ["URL", "vCard", "Phone"],
    features: [
      { title: "Dynamic redirects", body: "Mark a property “Sold” or relist it without replacing printed signs." },
      { title: "Per-listing analytics", body: "Track which listings and locations generate the most interest." },
      { title: "Folders & tags", body: "Keep codes tidy by agent, neighbourhood or campaign." },
    ],
  },
  {
    slug: "events",
    name: "Events",
    icon: TicketIcon,
    eyebrow: "QR codes for events",
    headline: "Faster check-in, fewer printed programmes",
    subhead:
      "Handle tickets, schedules, WiFi and feedback with codes you can change up to the minute as plans shift.",
    problem: {
      title: "Programmes change after they're printed",
      body: "Sessions move, rooms swap, speakers cancel. Printed agendas are out of date by day one. Dynamic QR codes keep attendees on the live schedule and speed up check-in lines.",
    },
    stat: { value: "Live", label: "schedule that updates after badges are printed" },
    ideas: [
      { title: "Ticketing & check-in", body: "Scan tickets at the door to cut queues and validate entry." },
      { title: "Live schedules", body: "A code on the badge or banner opens the current agenda — updated as it changes." },
      { title: "Session feedback", body: "Collect ratings per session while the talk is still fresh." },
      { title: "Networking vCards", body: "Attendees swap contact details with a single scan." },
      { title: "Venue WiFi & maps", body: "Connect guests to WiFi and point them to the right room instantly." },
    ],
    qrTypes: ["URL", "vCard", "WiFi"],
    features: [
      { title: "Dynamic redirects", body: "Move a session to a new room and the schedule code follows automatically." },
      { title: "Real-time analytics", body: "Watch check-ins and session interest land live during the event." },
      { title: "Bulk creation", body: "Generate codes for every session or badge at once via CSV or the API." },
    ],
  },
  {
    slug: "packaging",
    name: "Packaging",
    icon: CubeIcon,
    eyebrow: "QR codes for product packaging",
    headline: "Connected packaging that builds trust",
    subhead:
      "Give shoppers ingredients, recipes, sustainability data and proof of authenticity — pulled live, never reprinted.",
    problem: {
      title: "Labels can't hold everything — or stay current",
      body: "Regulations, allergen info and sourcing details change, and there's never enough room on the label. A dynamic QR code links to always-accurate information in the cloud and lets you update it after the product ships.",
    },
    stat: { value: "61%", label: "of consumers scan product QR codes after purchase" },
    ideas: [
      { title: "Ingredients & allergens", body: "Surface full ingredient lists and allergen warnings that you can keep current." },
      { title: "Recipes & how-to", body: "Inspire repeat use with recipes or usage guides tied to the product." },
      { title: "Sustainability & traceability", body: "Show sourcing, certifications and recycling info to back up your claims." },
      { title: "Authenticity checks", body: "Help customers verify a genuine product and fight counterfeits." },
      { title: "Reorder & register", body: "Make reordering or warranty registration a single scan." },
    ],
    qrTypes: ["URL", "Plain Text"],
    features: [
      { title: "Dynamic redirects", body: "Update allergen or recipe pages after packaging is printed and shipped." },
      { title: "Privacy-first analytics", body: "Measure engagement without ever storing a raw IP address." },
      { title: "API access", body: "Mint a unique code per SKU or batch programmatically." },
    ],
  },
  {
    slug: "education",
    name: "Education",
    icon: AcademicCapIcon,
    eyebrow: "QR codes for education",
    headline: "Connect handouts, campus and resources",
    subhead:
      "Link printed materials to digital resources, guide visitors around campus, and keep everything current term to term.",
    problem: {
      title: "Printed resources fall out of date",
      body: "Reading lists, schedules and lab guides change every term, but printed handouts don't. Dynamic QR codes let staff update the destination without reprinting a single sheet.",
    },
    stat: { value: "Term-ready", label: "resources you update once, reuse every year" },
    ideas: [
      { title: "Handout resources", body: "Link worksheets and posters to videos, reading lists and submission forms." },
      { title: "Library & catalogue", body: "Take students from a shelf label straight to the catalogue or e-book." },
      { title: "Campus wayfinding", body: "Place codes on maps and doors to guide visitors to the right place." },
      { title: "Lab & safety guides", body: "Surface up-to-date equipment instructions and safety procedures." },
      { title: "Event & enrolment sign-up", body: "Let students register for sessions or open days with one scan." },
    ],
    qrTypes: ["URL", "WiFi", "Email"],
    features: [
      { title: "Dynamic redirects", body: "Point last year's handout code at this year's materials — no reprint." },
      { title: "Folders & tags", body: "Organise codes by department, course or campus." },
      { title: "Real-time analytics", body: "See which resources students actually use." },
    ],
  },
];

export function getSolution(slug) {
  return SOLUTIONS.find((s) => s.slug === slug) || null;
}
